import { Badge, Box, SegmentedControl } from '@mantine/core';
import { useLayoutEffect, useRef, useState } from 'react';
import { brightness, mixHex } from '../../helpers/brandColors';
import { COLOR_ROLES, DARK_ROLES, safeWebFontUrl } from './brandIdentity';
import classes from './BrandIdentity.module.css';

// A sample email (header, body, button) and a small pop-up drawn straight from the draft, one mode at
// a time behind a Light / Dark switch so the sample stays large, so every change shows before it is saved. Plain boxes, not a template render:
// it is a likeness of the brand, not of any one campaign.
//
// Every painted element is tagged with the color roles it uses (data-role="colors.text …"), so the
// role the client is hovering or editing (`activeRole`, keyed like the form errors) can be outlined.

// Which saved role each palette slot comes from, per side.
const LIGHT_ROLES = {
  page: 'colors.page',
  content: 'colors.content',
  text: 'colors.text',
  textMuted: 'colors.textMuted',
  soft: 'colors.soft',
  highlight: 'colors.highlight',
  button: 'colors.primary',
  dark: 'colors.dark',
};
const DARK_SIDE_ROLES = {
  ...LIGHT_ROLES,
  page: 'darkColors.page',
  content: 'darkColors.content',
  text: 'darkColors.text',
  textMuted: 'darkColors.textMuted',
  soft: 'darkColors.soft',
  button: 'darkColors.link',
  dark: 'darkColors.dark',
};
const ROLE_LABELS = Object.fromEntries([
  ...COLOR_ROLES.map(({ key, label }) => [`colors.${key}`, label]),
  ...DARK_ROLES.map(({ key, label }) => [`darkColors.${key}`, label]),
]);

// A logo URL that no longer loads (moved on the site, hotlinking blocked) falls back to the name.
const Logo = ({ src, color, name }) => {
  const [failedSrc, setFailedSrc] = useState();
  return src && src !== failedSrc ? (
    <img
      src={src}
      alt={`${name} logo`}
      onError={() => setFailedSrc(src)}
      style={{ maxHeight: 28, maxWidth: 120, display: 'block' }}
    />
  ) : (
    <span style={{ fontWeight: 700, color }}>{name}</span>
  );
};

// The role's name sits on the top-right corner of the first element painted with it (the side
// itself when it is the page), so the caption and the outline read together.
const useTagPosition = (sideRef, activeRole) => {
  const [position, setPosition] = useState(null);
  useLayoutEffect(() => {
    const side = sideRef.current;
    const first = side?.matches('[data-active]') ? side : side?.querySelector('[data-active]');
    if (!side || !first) {
      setPosition(null);
      return;
    }
    const outer = side.getBoundingClientRect();
    const inner = first.getBoundingClientRect();
    setPosition({ top: Math.max(inner.top - outer.top - 9, 2), right: Math.max(outer.right - inner.right + 4, 2) });
  }, [sideRef, activeRole]);
  return position;
};

const Side = ({ label, hidden, palette, roles, activeRole, logoUrl, name, fontFamily, radius }) => {
  const r = `${radius}px`;
  const shown = Object.values(roles).includes(activeRole);
  const sideRef = useRef(null);
  const tagPosition = useTagPosition(sideRef, shown ? activeRole : null);
  const tag = (...slots) => {
    const tokens = [...new Set(slots.map((slot) => roles[slot]))];
    return { 'data-role': tokens.join(' '), 'data-active': shown && tokens.includes(activeRole) ? 'true' : undefined };
  };
  return (
    // Kept mounted while hidden, so switching modes keeps the font and logo loaded.
    <Box style={hidden ? { display: 'none' } : undefined}>
      <Box
        ref={sideRef}
        data-testid={`preview-${label.toLowerCase()}`}
        className={classes.previewSide}
        {...tag('page')}
        style={{
          background: palette.page,
          padding: 16,
          borderRadius: 8,
          border: '1px solid var(--mantine-color-default-border)',
          fontFamily,
          position: 'relative',
        }}
      >
        {shown && (
          <Badge
            // The field or chip in focus already announces the role; this caption is for the eye.
            aria-hidden
            className={classes.roleTag}
            size="sm"
            variant="filled"
            color="blue"
            tt="none"
            style={tagPosition ?? undefined}
          >
            {ROLE_LABELS[activeRole]}
          </Badge>
        )}
        <Box {...tag('content')} style={{ background: palette.content, borderRadius: r, overflow: 'hidden' }}>
          <Box
            style={{ padding: '12px 16px', borderBottom: `1px solid ${mixHex(palette.text, palette.content, 0.12)}` }}
          >
            <Logo src={logoUrl} color={palette.text} name={name} />
          </Box>
          <Box style={{ padding: '12px 14px' }}>
            <div
              {...tag('highlight')}
              style={{
                display: 'inline-block',
                background: palette.highlight,
                // A dark highlight (some brands use black) would hide dark text.
                color: brightness(palette.highlight) < 0.5 ? '#FFFFFF' : '#111',
                fontSize: 10,
                fontWeight: 700,
                padding: '2px 6px',
                borderRadius: r,
              }}
            >
              NEW
            </div>
            <div
              {...tag('text')}
              style={{ color: palette.text, fontSize: 16, fontWeight: 700, lineHeight: 1.25, margin: '8px 0 4px' }}
            >
              Picked for you this week
            </div>
            <div {...tag('textMuted')} style={{ color: palette.textMuted, fontSize: 12, lineHeight: 1.4 }}>
              A few things we think you will like, based on what you looked at last time.
            </div>
            <div
              {...tag('soft', 'text')}
              style={{
                background: palette.soft,
                borderRadius: r,
                padding: 10,
                margin: '10px 0',
                color: palette.text,
                fontSize: 12,
              }}
            >
              Free delivery on orders over 50
            </div>
            <div
              {...tag('button')}
              style={{
                display: 'inline-block',
                background: palette.button,
                color: '#FFFFFF',
                fontSize: 13,
                fontWeight: 600,
                padding: '8px 16px',
                borderRadius: r,
              }}
            >
              Shop now
            </div>
          </Box>
          <Box
            {...tag('dark')}
            style={{ background: palette.dark, color: '#FFFFFF', fontSize: 11, padding: '10px 16px', opacity: 0.95 }}
          >
            Questions? Just reply to this email.
          </Box>
        </Box>
        <Box
          data-testid="preview-popup"
          {...tag('content')}
          style={{
            marginTop: 12,
            marginLeft: 'auto',
            width: '80%',
            background: palette.content,
            borderRadius: r,
            padding: 12,
            boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
            textAlign: 'center',
          }}
        >
          <div {...tag('text')} style={{ color: palette.text, fontWeight: 700, fontSize: 14 }}>
            Get 10% off
          </div>
          <div {...tag('textMuted')} style={{ color: palette.textMuted, fontSize: 11, margin: '4px 0 8px' }}>
            Join our list for news and offers.
          </div>
          <div
            {...tag('button')}
            style={{ background: palette.button, color: '#FFFFFF', fontSize: 12, padding: '6px 10px', borderRadius: r }}
          >
            Sign me up
          </div>
        </Box>
      </Box>
    </Box>
  );
};

const BrandPreview = ({
  colors,
  darkColors,
  logoUrl,
  logoDarkUrl,
  name,
  fontFamily,
  webFontUrl,
  radius,
  activeRole,
}) => {
  const light = { ...colors, button: colors.primary };
  const dark = {
    ...colors,
    page: darkColors.page,
    content: darkColors.content,
    text: darkColors.text,
    textMuted: darkColors.textMuted,
    soft: darkColors.soft,
    button: darkColors.link,
    dark: darkColors.dark,
  };
  const font = fontFamily || 'inherit';
  const corners = Number.isFinite(radius) ? radius : 8;
  const safeFontUrl = safeWebFontUrl(webFontUrl);
  const [mode, setMode] = useState('light');
  // A role painted only in one mode switches the sample to it while it is pointed at or edited.
  const darkRoles = Object.values(DARK_SIDE_ROLES);
  const lightRoles = Object.values(LIGHT_ROLES);
  const forcedMode =
    activeRole && !lightRoles.includes(activeRole)
      ? 'dark'
      : activeRole && !darkRoles.includes(activeRole)
        ? 'light'
        : null;
  const shownMode = forcedMode ?? mode;

  return (
    <>
      {/* React hoists the stylesheet into <head>; only a known font host is loaded. */}
      {safeFontUrl && <link rel="stylesheet" href={safeFontUrl} precedence="default" />}
      <SegmentedControl
        size="xs"
        mb="sm"
        value={shownMode}
        onChange={setMode}
        aria-label="Preview mode"
        data={[
          { value: 'light', label: 'Light' },
          { value: 'dark', label: 'Dark' },
        ]}
      />
      <Box>
        <Side
          label="Light"
          hidden={shownMode !== 'light'}
          palette={light}
          roles={LIGHT_ROLES}
          activeRole={activeRole}
          logoUrl={logoUrl}
          name={name}
          fontFamily={font}
          radius={corners}
        />
        <Side
          label="Dark"
          hidden={shownMode !== 'dark'}
          palette={dark}
          roles={DARK_SIDE_ROLES}
          activeRole={activeRole}
          logoUrl={logoDarkUrl || logoUrl}
          name={name}
          fontFamily={font}
          radius={corners}
        />
      </Box>
    </>
  );
};

export default BrandPreview;
